/**
 * SCWT-PM-V2 Phase 3 — PWS Partner panel to invite Property Managers.
 *
 * Renders inside the PWS Partner Dashboard (via /partner/property-managers).
 * Only visible when `property_manager_v2` flag is ON.
 *
 * Actions:
 *   • Create invite (single-use, 30-day)
 *   • Copy invite URL + QR-ready link
 *   • Send preview email to self
 *   • Resend (mints new token, supersedes old)
 *   • Revoke pending
 */
import { useEffect, useMemo, useState } from "react";
import { Copy, Send, Loader2, RefreshCw, X, Mail, Building2 } from "lucide-react";
import api from "@/lib/api";

export default function PwsInviteAPmPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ agency_name: "", agency_email: "", agency_contact_name: "" });
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewStatus, setPreviewStatus] = useState({});   // { invId: "sending"/"sent"/"failed" }

  const load = async () => {
    try {
      const { data } = await api.get("/partner/pm-invitations");
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load invitations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const canCreate = useMemo(
    () => !creating && form.agency_name.trim() && form.agency_email.includes("@"),
    [creating, form]
  );

  const submit = async () => {
    setCreating(true); setError(null); setLastInviteUrl(null); setCopied(false);
    try {
      const { data } = await api.post("/partner/pm-invitations", {
        agency_name: form.agency_name.trim(),
        agency_email: form.agency_email.trim(),
        agency_contact_name: form.agency_contact_name.trim() || undefined,
      });
      setLastInviteUrl(data.invite_url);
      setForm({ agency_name: "", agency_email: "", agency_contact_name: "" });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create invitation");
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invitation? The link will stop working immediately.")) return;
    try {
      await api.post(`/partner/pm-invitations/${id}/revoke`, { reason: "" });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Revoke failed"); }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/partner/pm-invitations/${id}/resend`, {});
      setLastInviteUrl(data.invite_url);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Resend failed"); }
  };

  const sendTestPreview = async (id) => {
    setPreviewStatus((s) => ({ ...s, [id]: "sending" }));
    try {
      await api.post(`/partner/pm-invitations/${id}/send-test-email`, {});
      setPreviewStatus((s) => ({ ...s, [id]: "sent" }));
    } catch {
      setPreviewStatus((s) => ({ ...s, [id]: "failed" }));
    }
  };

  const copy = (url) => {
    try {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="pws-pm-invite-panel" className="space-y-6">
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900">Invite a Property Manager</h2>
        <p className="text-xs text-slate-500 mt-1">
          Send a free-onboarding link to a real-estate agency in your area.
          Aware creates the agency account and links it to your PWS Partner
          profile automatically.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="pws-invite-agency-name"
                  value={form.agency_name}
                  onChange={(e) => setForm((f) => ({ ...f, agency_name: e.target.value }))}
                  placeholder="Agency name"
                  className={inp} />
          <input data-testid="pws-invite-agency-email"
                  type="email"
                  value={form.agency_email}
                  onChange={(e) => setForm((f) => ({ ...f, agency_email: e.target.value }))}
                  placeholder="agency@example.com"
                  className={inp} />
          <input data-testid="pws-invite-agency-contact"
                  value={form.agency_contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, agency_contact_name: e.target.value }))}
                  placeholder="Contact name (optional)"
                  className={inp} />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            data-testid="pws-invite-create-btn"
            disabled={!canCreate}
            onClick={submit}
            className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white font-semibold rounded-full px-6 py-2.5 text-sm">
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            <Send className="w-4 h-4" /> Create invitation
          </button>
          {error && <span className="text-sm text-rose-700" data-testid="pws-invite-error">{error}</span>}
        </div>

        {lastInviteUrl && (
          <div data-testid="pws-invite-created" className="mt-5 rounded-xl bg-emerald-50 border border-emerald-100 p-4">
            <div className="text-xs font-semibold text-emerald-800 mb-2">
              Invitation created — copy the link and share with the agency:
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={lastInviteUrl}
                      data-testid="pws-invite-url"
                      className={`${inp} bg-white text-xs`} />
              <button onClick={() => copy(lastInviteUrl)}
                      data-testid="pws-invite-copy"
                      className="inline-flex items-center gap-1 border border-emerald-200 rounded-full px-3 py-2 text-xs text-emerald-800 hover:bg-emerald-100">
                <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900">Your invitations</h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>
        ) : items.length === 0 ? (
          <div data-testid="pws-invite-empty" className="p-8 text-center text-sm text-slate-500">
            No invitations yet. Create your first invite above.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((it) => (
              <li key={it.id} data-testid={`pws-invite-row-${it.id}`}
                  className="px-6 py-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" /> {it.agency_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate inline-flex items-center gap-1 mt-0.5">
                    <Mail className="w-3 h-3" /> {it.agency_email}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Created {it.created_at?.slice(0, 10)} · Expires {it.expires_at?.slice(0, 10)}
                    {it.resend_count > 0 && ` · Resent ${it.resend_count}×`}
                  </div>
                </div>
                <StatusChip status={it.status} />
                {it.status === "pending" && (
                  <div className="flex items-center gap-2">
                    <button data-testid={`pws-invite-preview-${it.id}`}
                            onClick={() => sendTestPreview(it.id)}
                            className="text-xs border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {previewStatus[it.id] || "Send preview"}
                    </button>
                    <button data-testid={`pws-invite-resend-${it.id}`}
                            onClick={() => resend(it.id)}
                            className="text-xs border border-cyan-200 text-cyan-800 rounded-full px-3 py-1.5 hover:bg-cyan-50 inline-flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> Resend
                    </button>
                    <button data-testid={`pws-invite-revoke-${it.id}`}
                            onClick={() => revoke(it.id)}
                            className="text-xs border border-rose-200 text-rose-700 rounded-full px-3 py-1.5 hover:bg-rose-50 inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Revoke
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";

function StatusChip({ status }) {
  const map = {
    pending:    "bg-amber-100 text-amber-800",
    accepted:   "bg-emerald-100 text-emerald-800",
    revoked:    "bg-slate-200 text-slate-700",
    expired:    "bg-slate-100 text-slate-500",
    superseded: "bg-slate-100 text-slate-500",
  };
  const cls = map[status] || "bg-slate-100 text-slate-600";
  return (
    <span data-testid={`pws-invite-status-${status}`}
          className={`inline-flex items-center rounded-full text-[11px] font-semibold px-2.5 py-1 uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}
